/**
 * WebMCP (experimental): expose site tools to browser agents via navigator.modelContext.
 * Spec/API still evolving; guard all calls so normal browsers are unaffected.
 */

type JsonSchema = Record<string, unknown>;

type WebMcpTool = {
  name: string;
  description: string;
  inputSchema: JsonSchema;
  execute: (args: Record<string, unknown>) => Promise<unknown> | unknown;
};

type ModelContext = {
  registerTool?: (tool: WebMcpTool) => void | Promise<void>;
  provideContext?: (ctx: { tools: WebMcpTool[] }) => void | Promise<void>;
};

function getModelContext(): ModelContext | null {
  if (typeof navigator === "undefined") return null;
  const nav = navigator as Navigator & { modelContext?: ModelContext };
  return nav.modelContext || null;
}

const PRODUCT_SUMMARY = {
  name: "QPTS",
  positioning: "Next-generation Electromagnetic Transient (EMT) simulation suite from Quark Power Inc.",
  highlights: [
    "Dual EMT engines: QPTS-L (large time-step) and QPTS-S (small time-step)",
    "Speed and auto-parallelization / HPC-ready architecture",
    "QPTrans: PSS/E RAW/DYR to QPTS conversion",
    "QPZScan: automated EMT frequency scan for SSO",
    "QPEdit: case edit, compare, and voltage benchmarking",
  ],
  urls: {
    home: "https://www.quarkpower.ca/",
    products: "https://www.quarkpower.ca/products",
    llms: "https://www.quarkpower.ca/llms.txt",
    brochure: "https://www.quarkpower.ca/docs/qpts-brochure.pdf",
    contact: "https://www.quarkpower.ca/contact",
  },
};

async function fetchLlmsTxt(): Promise<string> {
  const res = await fetch("/llms.txt", { credentials: "omit" });
  if (!res.ok) throw new Error(`Failed to load llms.txt (${res.status})`);
  return res.text();
}

export function registerWebMcpTools(): () => void {
  const ctx = getModelContext();
  if (!ctx) return () => undefined;

  const tools: WebMcpTool[] = [
    {
      name: "get_quarkpower_summary",
      description:
        "Return a short summary of Quark Power and QPTS next-generation EMT products, with key URLs.",
      inputSchema: {
        type: "object",
        properties: {},
        additionalProperties: false,
      },
      execute: async () => PRODUCT_SUMMARY,
    },
    {
      name: "get_llms_txt",
      description:
        "Fetch the full /llms.txt agent discovery document for Quark Power (product positioning, brochure highlights, benchmarks).",
      inputSchema: {
        type: "object",
        properties: {},
        additionalProperties: false,
      },
      execute: async () => ({ markdown: await fetchLlmsTxt() }),
    },
    {
      name: "navigate_quarkpower",
      description:
        "Navigate the Quark Power site to a known public path (home, products, blog, about, services, contact, privacy).",
      inputSchema: {
        type: "object",
        properties: {
          path: {
            type: "string",
            description: "Site path such as /products or /blog",
            enum: ["/", "/products", "/blog", "/about", "/services", "/contact", "/privacy"],
          },
        },
        required: ["path"],
        additionalProperties: false,
      },
      execute: async (args) => {
        const path = String(args.path || "/");
        window.location.assign(path);
        return { ok: true, path };
      },
    },
  ];

  const abort = new AbortController();

  (async () => {
    try {
      if (typeof ctx.registerTool === "function") {
        for (const tool of tools) {
          if (abort.signal.aborted) return;
          await ctx.registerTool(tool);
        }
      } else if (typeof ctx.provideContext === "function") {
        await ctx.provideContext({ tools });
      }
    } catch {
      // WebMCP unavailable or rejected — ignore
    }
  })();

  return () => abort.abort();
}
